import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@cbrebuild/blocks';
import verificationService from '../../../services/verification-service';
import {
  fetchAttachmentsDownloadLink
} from '../../../services/attachments-service';
import { DOC_TYPE_COMMISSION_AGREEMENT } from '../voucher-verify-constants';
import {
  getAttachmentName,
  getAttachmentExtension,
  getAttachmentExtensionClass,
  formatAttachmentDate,
} from '../voucher-verify-utils';
import './CommissionAttachmentSelector.scss';

const CommissionAttachmentSelector = ({
  dealId,
  selectedIds,
  onChange,
  isRequired,
  documentType,
  respectDocumentCategory,
  showAllFileTypes,
  isVoucherAttachment,
  voucherFiles,
  onUpload,
  autoSelectUploadedFile,
  showSelectedFiles,
  uploadTitle,
  allowEmailFiles,
}) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const normalizedSelectedIds = (selectedIds || [])
    .map(id => parseInt(id, 10))
    .filter(Number.isFinite);
  const selectedIdSet = new Set(normalizedSelectedIds);

  useEffect(() => {
    if (!dealId) return;

    setLoading(true);
    verificationService.fetchVerificationAttachments(dealId)
      .then((data) => {
        if (showAllFileTypes) {
          setAttachments(data || []);
          return;
        }
        const allowedTypes = new Set(['pdf', 'jpeg', 'png', 'jpg', ...(allowEmailFiles ? ['eml'] : [])]);
        const filtered = (data || []).filter((att) => {
          const fileType = (att.file_type || '').toLowerCase();
          const fileExtension = getAttachmentExtension(att).toLowerCase();
          return allowedTypes.has(fileType) || allowedTypes.has(fileExtension);
        });
        setAttachments(filtered);
      })
      .catch(() => {
        setAttachments([]);
      })
      .finally(() => setLoading(false));
  }, [dealId, allowEmailFiles, showAllFileTypes]);

  const availableAttachments = [
    ...new Map(
      [...attachments, ...(isVoucherAttachment ? voucherFiles || [] : [])]
        .map(attachment => [String(attachment.id), attachment])
    ).values(),
  ];

  // Keep the local copy of each file's category in step with what gets saved.
  const setLocalCategory = (attachmentId, category) => {
    if (!respectDocumentCategory) return;
    setAttachments(prev => prev.map(attachment => (
      parseInt(attachment.id, 10) === attachmentId
        ? { ...attachment, document_category: category }
        : attachment
    )));
  };

  // Yes/No changes which category is shown as checked: CA files for Yes, OTH files for No.
  const previousDocumentType = useRef(documentType);
  useEffect(() => {
    if (!respectDocumentCategory || loading || previousDocumentType.current === documentType) return;
    previousDocumentType.current = documentType;
    onChange(
      attachments
        .filter(attachment => attachment.document_category === documentType)
        .map(attachment => parseInt(attachment.id, 10))
    );
  }, [documentType, loading, attachments, respectDocumentCategory, onChange]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleSelectedAttachment = (attachmentId, checked) => {
    const normalizedId = parseInt(attachmentId, 10);
    if (!Number.isFinite(normalizedId)) return;

    if (checked) {
      if (!selectedIdSet.has(normalizedId)) {
        onChange([...normalizedSelectedIds, normalizedId]);
      }
      setLocalCategory(normalizedId, documentType);
    } else {
      onChange(normalizedSelectedIds.filter(id => id !== normalizedId));
      setLocalCategory(normalizedId, null);
    }
    setUploadError('');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file || !dealId) return;

    setUploadError('');
    setUploading(true);
    verificationService.uploadVerificationAttachment(
      dealId, file, documentType,
    )
      .then((createdAttachment) => {
        if (!createdAttachment?.id) {
          throw new Error('Upload did not return an attachment id.');
        }
        const attachment = respectDocumentCategory
          ? { ...createdAttachment, document_category: documentType }
          : createdAttachment;
        setAttachments(prev => [
          attachment,
          ...prev.filter(att => parseInt(att.id, 10) !== parseInt(createdAttachment.id, 10)),
        ]);
        if (onUpload) onUpload(createdAttachment);
        const uploadedId = parseInt(createdAttachment.id, 10);
        if (autoSelectUploadedFile && Number.isFinite(uploadedId) && !selectedIdSet.has(uploadedId)) {
          onChange([...normalizedSelectedIds, uploadedId]);
        }
        setIsDropdownOpen(false);
      })
      .catch((err) => {
        setUploadError(`Upload failed: ${err.message || 'Unknown error'}`);
      })
      .finally(() => {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      });
  };

  const selectedExistingAttachments = availableAttachments
    .filter(att => selectedIdSet.has(parseInt(att.id, 10)));

  const displayedAttachments = selectedExistingAttachments;
  const hasSelectedAttachment = displayedAttachments.length > 0;
  const selectedLabel = hasSelectedAttachment
    ? `${displayedAttachments.length} document${displayedAttachments.length > 1 ? 's' : ''} selected`
    : 'Select';

  const removeSelectedExistingAttachment = (attachmentId) => {
    onChange(normalizedSelectedIds.filter(id => id !== attachmentId));
    setLocalCategory(attachmentId, null);
  };

  if (loading) {
    return <div className="attachment-selector loading">Loading documents...</div>;
  }

  return (
    <div className="attachment-selector">

      <div className={`attachment-zone${isRequired && !hasSelectedAttachment ? ' has-error' : ''}`}>

        {availableAttachments.length > 0 && (
          <>
            <div className="attachment-dropdown-row" ref={dropdownRef}>
              <button
                type="button"
                className="attachment-dropdown-trigger"
                onClick={() => setIsDropdownOpen(prev => !prev)}
                aria-haspopup="listbox"
                aria-expanded={isDropdownOpen}
                aria-invalid={isRequired && !hasSelectedAttachment}
              >
                <span>{selectedLabel}</span>
                <span className={`attachment-dropdown-caret${isDropdownOpen ? ' open' : ''}`}>▾</span>
              </button>

              {isDropdownOpen && (
                <div
                  className="attachment-dropdown-panel"
                  role="listbox"
                  aria-label="Select commission agreement documents"
                  aria-multiselectable="true"
                >
                  {availableAttachments.map(att => (
                    <button
                      type="button"
                      key={att.id}
                      className="attachment-checkbox-item"
                      onClick={() => toggleSelectedAttachment(att.id, !selectedIdSet.has(parseInt(att.id, 10)))}
                      role="option"
                      aria-selected={selectedIdSet.has(parseInt(att.id, 10))}
                      tabIndex={0}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIdSet.has(parseInt(att.id, 10))}
                        onChange={e => toggleSelectedAttachment(att.id, e.target.checked)}
                        onClick={e => e.stopPropagation()}
                      />
                      <span>{att.document_name || att.filename || att.title || `Attachment ${att.id}`}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="attachment-zone-divider"><span>or</span></div>
          </>
        )}

        <div className="attachment-zone-upload">
          <div className="attachment-upload-info">
            <div className="attachment-upload-zone-title">{uploadTitle || 'Upload Commission Agreement'}</div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileUpload}
            disabled={uploading}
            style={{ display: 'none' }}
            id="commission-file-upload"
            accept={`.pdf,.png,.jpg,.jpeg${allowEmailFiles ? ',.eml' : ''}`}
          />
          <label htmlFor="commission-file-upload" className="btn-upload">
            {uploading ? 'Uploading...' : 'Browse file'}
          </label>
        </div>

      </div>

      {showSelectedFiles && displayedAttachments.length > 0 && displayedAttachments.map(attachment => (
        <div key={attachment.id} className="uploaded-attachment-row">
          <div className="attachment-file-icon">
            <Icon iconName="document" className="attachment-doc-icon" />
            <span className={`attachment-file-type ${getAttachmentExtensionClass(attachment)}`}>
              {getAttachmentExtension(attachment)}
            </span>
          </div>
          <div className="attachment-details">
            <div className="attachment-name-wrapper">
              <div className="attachment-name">
                {getAttachmentName(attachment)}
              </div>
              <span className="attachment-name-tooltip">{getAttachmentName(attachment)}</span>
            </div>
            <div className="attachment-date">
              {formatAttachmentDate(attachment.created || new Date().toISOString())}
            </div>
          </div>
          <button
            type="button"
            className="download-attachment"
            onClick={() => fetchAttachmentsDownloadLink(attachment.id, true)}
            aria-label="Download file"
          >
            <Icon iconName="download" />
          </button>
          <button
            type="button"
            className="delete-attachment"
            onClick={() => removeSelectedExistingAttachment(parseInt(attachment.id, 10))}
            aria-label="Remove selected file"
          >
            &#x2715;
          </button>
        </div>
      ))}

      {uploadError && (
        <div className="attachment-error">{uploadError}</div>
      )}
    </div>
  );
};

CommissionAttachmentSelector.propTypes = {
  dealId: PropTypes.number,
  selectedIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  onChange: PropTypes.func.isRequired,
  isRequired: PropTypes.bool,
  documentType: PropTypes.string,
  respectDocumentCategory: PropTypes.bool,
  showAllFileTypes: PropTypes.bool,
  isVoucherAttachment: PropTypes.bool,
  voucherFiles: PropTypes.arrayOf(PropTypes.shape({})),
  onUpload: PropTypes.func,
  autoSelectUploadedFile: PropTypes.bool,
  showSelectedFiles: PropTypes.bool,
  uploadTitle: PropTypes.string,
  allowEmailFiles: PropTypes.bool,
};

CommissionAttachmentSelector.defaultProps = {
  dealId: null,
  isRequired: false,
  documentType: DOC_TYPE_COMMISSION_AGREEMENT,
  respectDocumentCategory: false,
  showAllFileTypes: false,
  isVoucherAttachment: false,
  voucherFiles: [],
  onUpload: null,
  autoSelectUploadedFile: true,
  showSelectedFiles: true,
  uploadTitle: null,
  allowEmailFiles: false,
};

export default CommissionAttachmentSelector;
